"use client";

import { ArrowUpRight, Loader, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactMethods = [{
  label: "OFFICE",
  value: "123 Voltage Way, Power City, ST 90210",
  icon: MapPin
}, {
  label: "EMAIL",
  value: "dispatch@currentpulse.com",
  icon: Mail
}, {
  label: "PHONE",
  value: "(555) 888-POWER",
  icon: Phone
}];
function ReachOutForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-primary bg-primary/5">
        <p className="font-mono text-xs tracking-widest uppercase text-primary mb-3">
          [ REQUEST RECEIVED ]
        </p>
        <p className="font-default text-xl text-foreground mb-6 leading-snug">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-default text-xs tracking-widest uppercase border-border bg-transparent hover:bg-secondary text-foreground">
          Send Another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-7">
      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select a service type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              01 / Service Category
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <FormControl>
                <SelectTrigger className="w-full h-12 bg-transparent border-0 border-b border-border px-0 font-default text-base text-foreground focus-visible:ring-0 focus-visible:border-primary">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="Residential" className="font-default">Residential</SelectItem>
                <SelectItem value="Commercial" className="font-default">Commercial</SelectItem>
                <SelectItem value="Emergency Repair" className="font-default">Emergency Repair</SelectItem>
                <SelectItem value="Consultation" className="font-default">Consultation</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                02 / Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} className="h-12 bg-transparent border-0 border-b border-border px-0 font-default text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                03 / Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="h-12 bg-transparent border-0 border-b border-border px-0 font-default text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              04 / Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 000-0000" {...field} className="h-12 bg-transparent border-0 border-b border-border px-0 font-default text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
              05 / Project Details
            </FormLabel>
            <FormControl>
              <Textarea rows={6} placeholder="Describe your electrical needs..." {...field} className="min-h-[140px] bg-transparent border-0 border-b border-border px-0 py-3 font-default text-base text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-primary resize-none" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive bg-destructive/10">
          <p className="font-mono text-xs text-destructive">{submitError}</p>
        </div>}

      <div className="pt-2">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-4">
          [ We respond within 24 hours ]
        </p>
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-sm font-bold tracking-[0.15em] uppercase shadow-2xl">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : <>Request Service Now →</>}
        </Button>
      </div>
    </Form>;
}
export default function RequestService() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.1
  });
  return <section data-section-id="4170" id="request-service" className="bg-background text-foreground relative overflow-hidden py-20 md:py-28 lg:py-32 px-4 sm:px-6 lg:px-10">
      <div ref={ref} className={`max-w-[1400px] mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6 flex flex-col">
            <div className="flex items-center gap-3 mb-10">
              <span className="h-px w-10 bg-primary" />
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary">
                01 — Contact
              </p>
            </div>

            <h2 className="font-default font-bold uppercase leading-[0.9] tracking-tight text-foreground mb-10">
              <span className="block text-[clamp(3rem,8vw,9rem)]">POWER /</span>
              <span className="block text-[clamp(3rem,8vw,9rem)]">YOUR /</span>
              <span className="block text-[clamp(3rem,8vw,9rem)] font-bold italic">
                future.
              </span>
            </h2>

            <p className="font-default text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl mb-12">
              Current Pulse Electric provides precision electrical solutions for modern infrastructure. Whether you need emergency repairs or complex commercial installations, our licensed team is ready to energize your project.
            </p>

            <div className="space-y-4">
              {contactMethods.map((method, i) => {
              const Icon = method.icon;
              return <div key={i} data-index={i} className="group relative bg-card border border-border overflow-hidden transition-all duration-500 hover:border-primary">
                    <span className="absolute left-0 top-0 h-full w-0 bg-primary transition-all duration-500 group-hover:w-1" />
                    <div className="flex items-center justify-between gap-6 px-6 py-6 md:px-8 md:py-7">
                      <div className="flex items-center gap-5 min-w-0">
                        <Icon className="size-4 text-primary shrink-0" strokeWidth={1.5} />
                        <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground shrink-0 hidden sm:inline">
                          {method.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 min-w-0 flex-1 justify-end">
                        <p className="font-default text-sm md:text-base text-foreground text-right truncate">
                          {method.value}
                        </p>
                        <ArrowUpRight className="size-4 text-primary shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative p-8 md:p-12 border border-border bg-secondary">
              <div className="flex items-center justify-between mb-10 pb-6 border-b border-border/20">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                  [ Service Request ]
                </p>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-primary">
                  v.01
                </p>
              </div>

              <FormProvider formKey="request_service_form_lets_talk" formId="request_service_form_lets_talk" sectionName="request-service" defaultValues={{
              name: "",
              email: "",
              phone: "",
              inquiry_type: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              inquiry_type: {
                required: true
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Request Service Now" successMessage="Your request has been received. A technician will contact you shortly." errorMessage="Submission failed. Please call us directly.">
                <ReachOutForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}